import { Injectable } from '@angular/core';   // keep whatever decorator/imports you already have

@Injectable({ providedIn: 'root' })
export class TimerService {
  /** Milliseconds counted before the current run (i.e. across earlier pauses). */
  private accumulatedMs = 0;
  /** performance.now() when the current run began; null while paused or reset. */
  private startedAt: number | null = null;

  /** Start, or resume from where it was paused. Safe to call repeatedly. */
  startTimer(): void {
    if (this.startedAt === null) {
      this.startedAt = performance.now();
    }
  }

  /** Alias of startTimer(), for readability at call sites. */
  resumeTimer(): void {
    this.startTimer();
  }

  /** Freeze the elapsed time. Safe to call repeatedly. */
  pauseTimer(): void {
    if (this.startedAt === null) return;
    this.accumulatedMs += performance.now() - this.startedAt;
    this.startedAt = null;
  }

  /** Back to 00:00 and stopped. */
  resetTimer(): void {
    this.accumulatedMs = 0;
    this.startedAt = null;
  }

  /** True only while counting (false when paused or reset). */
  isRunning(): boolean {
    return this.startedAt !== null;
  }

  isPaused(): boolean {
    return this.startedAt === null && this.accumulatedMs > 0;
  }

  /** Elapsed whole seconds. Computed from timestamps on each call, so there is no interval. */
  getElapsedTime(): number {
    const current = this.startedAt === null ? 0 : performance.now() - this.startedAt;
    return Math.floor((this.accumulatedMs + current) / 1000);
  }

  getElapsedTimeFormatted(): string {
    const total = this.getElapsedTime();
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return hours > 0
      ? `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`
      : `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  private pad(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
  }
}